/**
 * Update Notification Component
 * Global notification for stale chunks — shown on every page, including
 * chat. Lives outside the omnibar so its refresh button is unaffected by
 * omnibar focus/active state and outside-click handling.
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateStore } from '../../stores/updateStore';
import { versionPollingService } from '../../services/versionPolling.service';

export const UpdateNotification: React.FC = () => {
  const { t } = useTranslation();
  const { shouldShowNotification, dismissUpdate, setUpdateRequired } = useUpdateStore();

  // Listen for version changes globally — including chat pages. The notice
  // is rendered as a top-level fixed bar above the omnibar so its lifecycle
  // is decoupled from the omnibar's active/outside-click state.
  useEffect(() => {
    const unsubscribe = versionPollingService.onVersionChange((newVersion, oldVersion) => {
      setUpdateRequired(true, newVersion, oldVersion);
    });
    return () => unsubscribe();
  }, [setUpdateRequired]);

  if (!shouldShowNotification()) {
    return null;
  }

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    dismissUpdate();
  };

  return (
    <div className="update-notification-wrapper">
      <div className="update-notification">
        <div className="update-notification__indicator">
          <span className="update-notification__dot" aria-label={t('common.updateNotification.updateAvailable')}></span>
        </div>

        <div className="update-notification__content">
          <p className="update-notification__message">
            {t('common.updateNotification.message')}
          </p>
        </div>

        <div className="update-notification__actions">
          <button
            onClick={handleRefresh}
            className="update-notification__button update-notification__button--primary"
            type="button"
          >
            {t('common.updateNotification.refreshNow')}
          </button>
          <button
            onClick={handleDismiss}
            className="update-notification__button update-notification__button--secondary"
            type="button"
          >
            {t('common.updateNotification.later')}
          </button>
        </div>
      </div>

      <style jsx>{`
        .update-notification-wrapper {
          position: fixed;
          top: 16px;
          right: 16px;
          z-index: 9999;
          pointer-events: none;
        }

        .update-notification {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--color-amber-50, #fffbeb);
          border: 1px solid var(--color-amber-300, #fcd34d);
          border-radius: var(--border-radius-lg, 8px);
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          pointer-events: auto;
          max-width: 500px;
          animation: slideInRight 0.3s ease-out;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .update-notification__indicator {
          flex-shrink: 0;
        }

        .update-notification__dot {
          display: block;
          width: 12px;
          height: 12px;
          background: var(--color-amber-500, #f59e0b);
          border-radius: 50%;
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
        }

        .update-notification__content {
          flex: 1;
          min-width: 0;
        }

        .update-notification__message {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
          color: var(--color-amber-900, #78350f);
        }

        .update-notification__actions {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }

        .update-notification__button {
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 500;
          border-radius: var(--border-radius-md, 6px);
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .update-notification__button--primary {
          background: var(--color-amber-600, #d97706);
          color: white;
          border-color: var(--color-amber-600, #d97706);
        }

        .update-notification__button--primary:hover {
          background: var(--color-amber-700, #b45309);
          border-color: var(--color-amber-700, #b45309);
        }

        .update-notification__button--primary:active {
          transform: scale(0.98);
        }

        .update-notification__button--secondary {
          background: transparent;
          color: var(--color-amber-800, #92400e);
          border-color: var(--color-amber-300, #fcd34d);
        }

        .update-notification__button--secondary:hover {
          background: var(--color-amber-100, #fef3c7);
        }

        .update-notification__button--secondary:active {
          transform: scale(0.98);
        }

        /* Mobile responsive */
        @media (max-width: 640px) {
          .update-notification-wrapper {
            top: 8px;
            right: 8px;
            left: 8px;
          }

          .update-notification {
            flex-direction: column;
            align-items: stretch;
            gap: 8px;
            padding: 12px;
          }

          .update-notification__actions {
            flex-direction: column;
            gap: 6px;
          }

          .update-notification__button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default UpdateNotification;
